// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import { formatAngle, formatArea, formatLength, summarize, type MeasureState, type Measurement, type Vec3 } from '../../bygning/ruler';
import { SNAP_LABEL } from '../../bygning/snapping';
import type { ViewportHandle } from '../../viewport/Viewport';
import styles from './MeasureLayer.module.css';
import type { SnapResult } from './useMeasureTool';

/** A segment shorter than this on screen (px) carries no length label: it would cover its own ends. */
const MIN_LABEL_SEGMENT = 64;

export interface MeasureLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  state: MeasureState;
  /** Where the pointer would put a point (the snap indicator), if anywhere. */
  hover: SnapResult | null;
  /** A tool is on: the vertices can be dragged and the draft follows the pointer. */
  editing: boolean;
  /** The view is the locked plan: only measurements taken in the current view (plan or 3D) can be edited. */
  plan: boolean;
  /** Where a pointer at canvas (x, y) lands, `free` without snapping. */
  snapAt: (x: number, y: number, free: boolean) => SnapResult | null;
  /** A vertex dragged to `point`. */
  onMoveVertex: (id: string, index: number, point: Vec3) => void;
  /** A vertex clicked without a drag (the draft's last or first point: finish). */
  onVertexClick: (id: string, index: number) => void;
  setHover: (hover: SnapResult | null) => void;
  /** Bump to reproject (content landed). */
  revision: number;
}

interface Drawn {
  m: Measurement;
  draft: boolean;
  /** Screen xy per point (NaN off screen), the preview point last on a draft. */
  xy: [number, number][];
  /** The live point under the pointer is part of `xy` (last). */
  preview: boolean;
}

const finite = (p: [number, number]) => Number.isFinite(p[0]) && Number.isFinite(p[1]);

/**
 * The measurements over the canvas (frontend-aa2): lines and points drawn
 * in SVG on top of the model — never hidden by it — with a halo in the
 * surface colour so they read on the scan, the mesh and the page in both
 * themes, a fixed pixel size whatever the projection or zoom, and the
 * lengths and areas as pills. The draft follows the pointer; the snap
 * indicator says what the point would land on (punkt / hjørne / væg /
 * åbning). Reprojected on every camera change. Only the vertex handles take
 * the pointer, and only while a tool is on.
 */
export function MeasureLayer({ viewport, state, hover, editing, plan, snapAt, onMoveVertex, onVertexClick, setHover, revision }: MeasureLayerProps) {
  const [tick, setTick] = useState(0);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame: number | null = null;
    const bump = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(bump);
    if (hostRef.current) observer?.observe(hostRef.current);
    bump();
    return () => {
      off();
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [viewport, revision]);

  const handle = viewport.current;
  const drawn = useMemo(() => {
    const out: Drawn[] = [];
    if (!handle) return { list: out, hover: null as [number, number] | null };
    const project = (pts: readonly Vec3[]): [number, number][] => {
      const xyz = new Float64Array(pts.length * 3);
      pts.forEach((p, i) => xyz.set(p, i * 3));
      const s = handle.projectWorld(xyz, { clip: false });
      return pts.map((_, i) => [s[i * 2], s[i * 2 + 1]]);
    };
    for (const m of state.items) out.push({ m, draft: false, xy: project(m.points), preview: false });
    const d = state.draft;
    if (d) {
      const preview = editing && hover !== null;
      const points = preview && hover ? [...d.points, d.plan ? ([hover.world[0], hover.world[1], d.points[0][2]] as Vec3) : hover.world] : d.points;
      out.push({ m: { ...d, points }, draft: true, xy: project(points), preview });
    }
    const hoverXy = hover ? project([hover.world])[0] : null;
    return { list: out, hover: hoverXy && finite(hoverXy) ? hoverXy : null };
    // `tick` is the camera; the handle's projection reads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, state.items, state.draft, hover, editing, tick]);

  // --- vertex drags --------------------------------------------------------------

  const drag = useRef<{ id: string; index: number; pointer: number; target: Element; x: number; y: number; moved: boolean } | null>(null);
  const release = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    try {
      if (d.target.hasPointerCapture(d.pointer)) d.target.releasePointerCapture(d.pointer);
    } catch {
      /* the element is gone */
    }
    document.body.classList.remove(styles.dragging);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => release(), []);
  useEffect(() => {
    if (!editing) release();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const startDrag = (id: string, index: number) => (event: ReactPointerEvent<SVGElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as Element;
    target.setPointerCapture(event.pointerId);
    document.body.classList.add(styles.dragging);
    drag.current = { id, index, pointer: event.pointerId, target, x: event.clientX, y: event.clientY, moved: false };
  };
  const moveDrag = (event: ReactPointerEvent<SVGElement>) => {
    const d = drag.current;
    const rect = hostRef.current?.getBoundingClientRect();
    if (!d || !rect || event.pointerId !== d.pointer) return;
    // A press that has not left its spot is still a click.
    if (!d.moved && Math.hypot(event.clientX - d.x, event.clientY - d.y) <= 4) return;
    d.moved = true;
    const hit = snapAt(event.clientX - rect.left, event.clientY - rect.top, event.altKey);
    setHover(hit);
    if (hit) onMoveVertex(d.id, d.index, hit.world);
  };
  const endDrag = (event: ReactPointerEvent<SVGElement>) => {
    const d = drag.current;
    if (!d || event.pointerId !== d.pointer) return;
    event.stopPropagation();
    release();
    setHover(null);
    if (!d.moved && event.type === 'pointerup') onVertexClick(d.id, d.index);
  };

  // --- labels --------------------------------------------------------------------

  // `corner`: the pill sits up and to the right of (x, y), clear of the pointer and its snap label.
  const labels: { key: string; x: number; y: number; text: string; sub?: string; warning?: string; strong: boolean; draft: boolean; corner?: boolean; warn?: boolean }[] = [];
  for (const { m, draft, xy } of drawn.list) {
    const s = summarize(m);
    const n = xy.length;
    const segs = m.kind === 'area' && n > 2 ? n : n - 1;
    const single = m.kind === 'distance' && n === 2;
    for (let i = 0; i < segs; i += 1) {
      const a = xy[i];
      const b = xy[(i + 1) % n];
      if (!finite(a) || !finite(b) || Math.hypot(b[0] - a[0], b[1] - a[1]) < MIN_LABEL_SEGMENT) continue;
      labels.push({
        key: `${m.id}s${i}`,
        x: (a[0] + b[0]) / 2,
        y: (a[1] + b[1]) / 2,
        text: formatLength(s.segments[i] ?? 0),
        strong: single,
        draft,
      });
    }
    if (m.kind === 'distance' && n > 2 && finite(xy[n - 1])) {
      labels.push({ key: `${m.id}t`, x: xy[n - 1][0] + 10, y: xy[n - 1][1] - 10, text: `I alt ${formatLength(s.length)}`, strong: true, draft, corner: true });
    }
    if (m.kind === 'area' && (s.area !== null || s.selfIntersecting)) {
      const on = xy.filter(finite);
      if (on.length > 2) {
        const cx = on.reduce((sum, p) => sum + p[0], 0) / on.length;
        const cy = on.reduce((sum, p) => sum + p[1], 0) / on.length;
        if (s.selfIntersecting) {
          labels.push({ key: `${m.id}a`, x: cx, y: cy, text: 'Ugyldigt areal', sub: 'flagen krydser sig selv', strong: true, draft, warn: true });
        } else {
          const sub = [`omkreds ${formatLength(s.length)}`, ...(s.tiltDeg !== null ? [`hældning ${formatAngle(s.tiltDeg)}`] : [])].join(' · ');
          labels.push({ key: `${m.id}a`, x: cx, y: cy, text: formatArea(s.area ?? 0), sub, warning: s.warning ?? undefined, strong: true, draft, warn: s.warning !== null });
        }
      }
    }
  }

  const hoverKind = hover?.kind ?? null;

  return (
    <div ref={hostRef} className={styles.layer} aria-hidden="true">
      <svg className={styles.svg}>
        {drawn.list.map(({ m, draft, xy, preview }) => {
          const ok = xy.every(finite);
          const closed = m.kind === 'area' && xy.length > 2;
          const pts = xy.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return (
            <g key={m.id} className={draft ? styles.draft : undefined}>
              {ok && closed && <polygon className={styles.fill} points={pts} />}
              {ok && xy.length > 1 &&
                (closed ? (
                  <>
                    <polygon className={styles.halo} points={pts} />
                    <polygon className={styles.line} points={pts} />
                  </>
                ) : (
                  <>
                    <polyline className={styles.halo} points={pts} />
                    <polyline className={styles.line} points={pts} />
                  </>
                ))}
              {!ok &&
                xy.slice(1).map((b, i) => {
                  const a = xy[i];
                  if (!finite(a) || !finite(b)) return null;
                  return <line key={i} className={styles.line} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />;
                })}
              {xy.map((p, i) => {
                if (!finite(p) || (preview && i === xy.length - 1)) return null;
                return (
                  <g key={i}>
                    <circle className={styles.vertex} cx={p[0]} cy={p[1]} r={4} />
                    {editing && m.plan === plan && (
                      <circle
                        className={styles.vertexHandle}
                        cx={p[0]}
                        cy={p[1]}
                        r={9}
                        onPointerDown={startDrag(m.id, i)}
                        onPointerMove={moveDrag}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                      >
                        <title>Træk for at flytte punktet</title>
                      </circle>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
        {drawn.hover && (
          <g className={styles.snap} transform={`translate(${drawn.hover[0].toFixed(1)} ${drawn.hover[1].toFixed(1)})`}>
            {hoverKind === null && <path className={styles.snapFree} d="M-7 0H7M0 -7V7" />}
            {hoverKind === 'punkt' && <circle className={styles.snapMark} r={4.5} />}
            {hoverKind === 'hjørne' && <rect className={styles.snapMark} x={-5.5} y={-5.5} width={11} height={11} />}
            {hoverKind === 'midtpunkt' && <path className={styles.snapMark} d="M0 -6.5L6 4.5H-6Z" />}
            {hoverKind === 'væg' && <path className={styles.snapMark} d="M0 -7L7 0L0 7L-7 0Z" />}
            {hoverKind === 'åbning' && (
              <>
                <circle className={styles.snapMark} r={6.5} />
                <circle className={styles.snapDot} r={2} />
              </>
            )}
          </g>
        )}
      </svg>
      {labels.map((l) => (
        <span
          key={l.key}
          className={[styles.label, l.strong ? styles.labelStrong : '', l.warn ? styles.labelWarn : '', l.draft ? styles.labelDraft : ''].filter(Boolean).join(' ')}
          style={{ transform: `translate(${l.x.toFixed(1)}px, ${l.y.toFixed(1)}px) ${l.corner ? 'translate(0, -100%)' : 'translate(-50%, -50%)'}` }}
        >
          <span className={styles.value}>{l.text}</span>
          {l.sub && <span className={styles.sub}>{l.sub}</span>}
          {l.warning && <span className={styles.sub}>⚠ {l.warning}</span>}
        </span>
      ))}
      {drawn.hover && hoverKind !== null && (
        <span
          className={styles.snapLabel}
          style={{ transform: `translate(${(drawn.hover[0] + 12).toFixed(1)}px, ${(drawn.hover[1] + 10).toFixed(1)}px)` }}
        >
          {SNAP_LABEL[hoverKind]}
        </span>
      )}
    </div>
  );
}
